// Inline SVG flags for the language selector — bundled (no external assets),
// crisp at any size. Drawn in a 60×40 (3:2) viewBox, clipped to a rounded chip.

export type CountryCode = 'ng' | 'gh' | 'gb' | 'sn' | 'rw';

export interface Language {
    code: CountryCode;
    name: string;
}

// Order matches the design reference.
export const LANGUAGES: Language[] = [
    { code: 'ng', name: 'Nigeria' },
    { code: 'gh', name: 'Ghana' },
    { code: 'gb', name: 'United Kingdom' },
    { code: 'sn', name: 'Senegal' },
    { code: 'rw', name: 'Rwanda' },
];

/** Five-pointed star centred at (cx,cy) scaled to `size` (roughly its height). */
function Star({ cx, cy, size, fill }: { cx: number; cy: number; size: number; fill: string }) {
    const s = size / 22; // base path spans ~22 units
    return (
        <path
            d="M12 2l2.9 6.9 7.1.6-5.4 4.7 1.6 7-6.2-3.7-6.2 3.7 1.6-7L2 9.5l7.1-.6z"
            fill={fill}
            transform={`translate(${cx} ${cy}) scale(${s}) translate(-12 -12)`}
        />
    );
}

function NigeriaSvg() {
    return (
        <>
            <rect width="20" height="40" fill="#008751" />
            <rect x="20" width="20" height="40" fill="#fff" />
            <rect x="40" width="20" height="40" fill="#008751" />
        </>
    );
}

function GhanaSvg() {
    return (
        <>
            <rect width="60" height="13.34" fill="#ce1126" />
            <rect y="13.34" width="60" height="13.33" fill="#fcd116" />
            <rect y="26.67" width="60" height="13.33" fill="#006b3f" />
            <Star cx={30} cy={20} size={13} fill="#000" />
        </>
    );
}

/** Simplified Union Jack — recognisable at chip size. */
function UkSvg() {
    return (
        <>
            <rect width="60" height="40" fill="#012169" />
            <path d="M0 0 60 40M60 0 0 40" stroke="#fff" strokeWidth="8" />
            <path d="M0 0 60 40M60 0 0 40" stroke="#c8102e" strokeWidth="3.2" />
            <path d="M30 0V40M0 20H60" stroke="#fff" strokeWidth="10" />
            <path d="M30 0V40M0 20H60" stroke="#c8102e" strokeWidth="6" />
        </>
    );
}

function SenegalSvg() {
    return (
        <>
            <rect width="20" height="40" fill="#00853f" />
            <rect x="20" width="20" height="40" fill="#fdef42" />
            <rect x="40" width="20" height="40" fill="#e31b23" />
            <Star cx={30} cy={20} size={13} fill="#00853f" />
        </>
    );
}

function RwandaSvg() {
    const rays = Array.from({ length: 24 }, (_, i) => i * 15);
    return (
        <>
            <rect width="60" height="20" fill="#00a1de" />
            <rect y="20" width="60" height="10" fill="#fad201" />
            <rect y="30" width="60" height="10" fill="#20603d" />
            {/* Golden sun in the upper hoist-right of the blue band */}
            {rays.map((a) => (
                <line
                    key={a}
                    x1="46"
                    y1="4.4"
                    x2="46"
                    y2="6.6"
                    stroke="#fad201"
                    strokeWidth="0.8"
                    transform={`rotate(${a} 46 10)`}
                />
            ))}
            <circle cx="46" cy="10" r="3" fill="#fad201" />
        </>
    );
}

const SVGS: Record<CountryCode, () => React.JSX.Element> = {
    ng: NigeriaSvg,
    gh: GhanaSvg,
    gb: UkSvg,
    sn: SenegalSvg,
    rw: RwandaSvg,
};

/** A flag chip. Size via `className` (e.g. `h-4 w-6`). */
export function Flag({ code, className = 'h-4 w-6' }: { code: CountryCode; className?: string }) {
    const Body = SVGS[code];
    return (
        <span className={`inline-flex shrink-0 overflow-hidden rounded-[2px] ${className}`} aria-hidden>
            <svg viewBox="0 0 60 40" className="h-full w-full" preserveAspectRatio="none">
                <Body />
            </svg>
        </span>
    );
}
